import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Plus, Trash, PencilSimple, Download, Check } from '@phosphor-icons/react';
import { getToken } from '@/utils/auth';
import { logger } from '@/utils/logger';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INITIAL_GAME = {
  name: '', image: '', genre: '', description: '', players: 0, platform: '', developer: ''
};

export const ManageGamesTab = ({ games, onRefresh, onEdit }) => {
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newGame, setNewGame] = useState(INITIAL_GAME);

  const createGame = async () => {
    if (!newGame.name || !newGame.genre) {
      toast.error('Name and genre are required');
      return;
    }
    setLoading(true);
    try {
      const token = getToken();
      await axios.post(`${API}/admin/games`, newGame, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Game created successfully!');
      setNewGame(INITIAL_GAME);
      setShowAddForm(false);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create game');
    } finally {
      setLoading(false);
    }
  };

  const deleteGame = async (gameId) => {
    if (!window.confirm('Are you sure you want to delete this game?')) return;
    try {
      const token = getToken();
      await axios.delete(`${API}/admin/games/${gameId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Game deleted successfully!');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete game');
    }
  };

  const exportGames = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/admin/games/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'games-export.json';
      a.click();
      toast.success('Games exported!');
    } catch (error) {
      logger.error('Export failed:', error);
      toast.error('Failed to export games');
    }
  };

  return (
    <div className="p-6 pt-4">
      <div className="flex justify-between mb-4">
        <Button onClick={() => setShowAddForm(true)} className="bg-[#0047FF] hover:bg-[#0037CC]">
          <Plus size={18} className="mr-2" /> Add Game
        </Button>
        <Button variant="outline" onClick={exportGames}>
          <Download size={18} className="mr-2" /> Export JSON
        </Button>
      </div>

      {showAddForm && (
        <div className="mb-6 p-4 border border-[#E4E4E7] bg-[#FAFAFA]">
          <h3 className="font-medium mb-4">Add New Game</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Name *</Label>
              <Input value={newGame.name} onChange={(e) => setNewGame({...newGame, name: e.target.value})} />
            </div>
            <div>
              <Label>Genre *</Label>
              <Input value={newGame.genre} onChange={(e) => setNewGame({...newGame, genre: e.target.value})} />
            </div>
            <div>
              <Label>Image URL</Label>
              <Input value={newGame.image} onChange={(e) => setNewGame({...newGame, image: e.target.value})} />
            </div>
            <div>
              <Label>Platform</Label>
              <Input value={newGame.platform} onChange={(e) => setNewGame({...newGame, platform: e.target.value})} />
            </div>
            <div className="col-span-2">
              <Label>Description</Label>
              <Textarea value={newGame.description} onChange={(e) => setNewGame({...newGame, description: e.target.value})} />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={createGame} disabled={loading} className="bg-[#0047FF]">
              <Check size={18} className="mr-2" /> Create
            </Button>
            <Button variant="outline" onClick={() => setShowAddForm(false)}>Cancel</Button>
          </div>
        </div>
      )}

      <ScrollArea className="h-[400px]">
        <div className="space-y-2">
          {games.map((game) => (
            <div key={game.id} className="flex items-center gap-4 p-3 border border-[#E4E4E7] hover:bg-[#FAFAFA]">
              <img src={game.image} alt={game.name} className="w-16 h-16 object-cover" />
              <div className="flex-1">
                <p className="font-medium text-[#09090B]">{game.name}</p>
                <p className="text-sm text-[#71717A]">{game.genre} • {game.source || 'manual'}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => onEdit(game)}>
                <PencilSimple size={18} />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => deleteGame(game.id)}>
                <Trash size={18} className="text-red-500" />
              </Button>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};
